import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";

import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";

import {
  FaSearch,
  FaFilter,
  FaRedo,
  FaEye,
  FaBoxOpen,
} from "react-icons/fa";

import {
  BookOpen,
} from "lucide-react";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import axios from "axios";

import {
  ThemeContext,
} from "../../context/ThemeContext";

import getCurrentSkin from "../../utils/getCurrentSkin";

const LedgerMainReport = () => {
  /* =========================================================
     THEME
  ========================================================= */

  const {
    isDark,
    skin,
    setSkin,
  } = useContext(ThemeContext);

  const currentSkin =
    getCurrentSkin(skin);

  const {
    baseColor,
    baseShade,
  } = currentSkin;

  /* =========================================================
     LOCAL STORAGE
  ========================================================= */

  const finYearId =
    localStorage.getItem(
      "finYearId"
    ) || "";

  const finYear =
    localStorage.getItem(
      "finYear"
    ) || "";

  const storedBranchId =
    localStorage.getItem(
      "branchId"
    ) || "8";

  const storedBranchType =
    localStorage.getItem(
      "branchType"
    ) || "";

  const storedBranch =
    localStorage.getItem(
      "branch"
    ) || "";

  const userId =
    localStorage.getItem(
      "userId"
    ) || "";

  /* =========================================================
     BRANCH TYPE
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const initialBranchType =
    storedBranch ===
      "HEAD-OFFICE" ||
    storedBranchType ===
      "HO"
      ? "HO"
      : "Branch";

  /* =========================================================
     COMMON HEADERS
  ========================================================= */

  const commonHeaders =
    useMemo(
      () => ({
        "x-finyearid":
          finYearId,

        "x-finyear":
          finYear,

        "x-branchid":
          storedBranchId,

        "x-branch":
          storedBranchType ||
          initialBranchType,

        "x-userid":
          userId,
      }),
      [
        finYearId,
        finYear,
        storedBranchId,
        storedBranchType,
        initialBranchType,
        userId,
      ]
    );

  /* =========================================================
     FILTER STATE
  ========================================================= */

  const [
    branchType,
    setBranchType,
  ] = useState(
    initialBranchType
  );

  const [
    branchId,
    setBranchId,
  ] = useState(
    storedBranchId ||
      "8"
  );

  const [
    location,
    setLocation,
  ] = useState(
    initialBranchType ===
      "HO"
      ? "HO"
      : "BRANCH"
  );

  const [
    fromDate,
    setFromDate,
  ] = useState("");

  const [
    toDate,
    setToDate,
  ] = useState("");

  const [
    supplierEquipment,
    setSupplierEquipment,
  ] = useState("");

  const [
    vendorType,
    setVendorType,
  ] = useState("");

  const [
    vehicleMainType,
    setVehicleMainType,
  ] = useState("");

  const [
    vehicleType,
    setVehicleType,
  ] = useState("");

  const [
    equipmentType,
    setEquipmentType,
  ] = useState("");

  const [
    status,
    setStatus,
  ] = useState("");

  const [
    vehicleCode,
    setVehicleCode,
  ] = useState("");

  const [
    store,
    setStore,
  ] = useState("");

  const [
    supplier,
    setSupplier,
  ] = useState("");

  /* =========================================================
     UI STATE
  ========================================================= */

  const [
    showFilters,
    setShowFilters,
  ] = useState(true);

  const [
    loading,
    setLoading,
  ] = useState(false);

  /* =========================================================
     DROPDOWN + DATA STATE
  ========================================================= */

  const [
    branchList,
    setBranchList,
  ] = useState([]);

  const [
    supplierList,
    setSupplierList,
  ] = useState([]);

  const [
    data,
    setData,
  ] = useState([]);

  /* =========================================================
     API BRANCH TYPE
  ========================================================= */

  const apiBranchType =
    branchType === "HO"
      ? "HO"
      : "Branch";

  const payloadBranchType =
    branchType === "HO"
      ? "H"
      : "B";

  /* =========================================================
     TEMPLATE INPUT CLASSES
  ========================================================= */

  const inputClass = `
    w-full
    px-3
    py-2.5
    rounded-lg
    border
    text-sm
    transition-colors
    focus:outline-none
    focus:ring-2
    focus:ring-${baseColor}-500/30
    focus:border-${baseColor}-500
    ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }
  `;

  const labelClass = `
    block
    text-xs
    font-semibold
    tracking-wide
    mb-1.5
    ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }
  `;

  /* =========================================================
     LOAD STORES + SUPPLIERS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  useEffect(() => {
    setLoading(true);

    axios
      .get(
        "https://nissi-india.com/api/po_fillDropdowns",
        {
          params: {
            branchid:
              branchId ||
              storedBranchId ||
              8,

            branchtype:
              apiBranchType,
          },

          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        const stores =
          res.data.data
            ?.stores ||
          [];

        const suppliers =
          res.data.data
            ?.suppliers ||
          [];

        const defaultStoreId =
          res.data.data
            ?.defaultStoreId;

        setBranchList(
          stores
        );

        setSupplierList(
          suppliers
        );

        if (
          defaultStoreId &&
          !store
        ) {
          setStore(
            defaultStoreId.toString()
          );

          setBranchId(
            defaultStoreId.toString()
          );
        }
      })
      .catch((err) => {
        console.error(
          "Dropdown fetch failed",
          err
        );

        toast.error(
          "Failed to load dropdowns"
        );
      })
      .finally(() => {
        setLoading(false);
      });

    // original logic depends on branch type
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchType]);

  /* =========================================================
     SEARCH
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleSearch = () => {
    setLoading(true);

    const payload = {
      branchType:
        payloadBranchType,

      sessionBranchId:
        String(
          storedBranchId ||
            branchId ||
            "8"
        ),

      selectedBranchId:
        String(
          store ||
            branchId ||
            storedBranchId ||
            "8"
        ),

      type:
        supplierEquipment ===
        "Equipment"
          ? "2"
          : "1",

      rptTypeInput:
        "",

      vehcode:
        vehicleCode ||
        "",

      vtname:
        vehicleType ||
        "",

      vmaintname:
        vehicleMainType ||
        "",

      optionHB:
        equipmentType ||
        "",

      venderType:
        vendorType ||
        "%",

      supcode:
        supplier ||
        "",

      fromDate:
        fromDate ||
        "",

      toDate:
        toDate ||
        "",

      location:
        location ||
        "",

      status:
        status ||
        "",
    };

    console.log(
      "payload",
      payload
    );

    axios
      .post(
        "https://nissi-india.com/api/ledger/search",
        payload,
        {
          headers:
            commonHeaders,
        }
      )
      .then((res) => {
        const rows =
          res.data
            ?.sections
            ?.supplier
            ?.rows ||
          res.data
            ?.rows ||
          res.data
            ?.recordset ||
          res.data
            ?.recordsets?.[0] ||
          [];

        setData(
          rows
        );

        console.log(
          "response:",
          res.data
        );

        toast.success(
          `Loaded ${rows.length} records`
        );
      })
      .catch((err) => {
        console.error(
          "Ledger fetch failed",
          err
        );

        toast.error(
          "Failed to fetch ledger listing"
        );

        setData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  /* =========================================================
     OPEN SUB REPORT
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleOpenSubReport =
    (row) => {
      localStorage.setItem(
        "ledgerSupplier",
        JSON.stringify(row)
      );

      window.open(
        "/inventory/LedgerSubReport",
        "_blank",
        "noopener,noreferrer"
      );
    };

  /* =========================================================
     BRANCH TYPE CHANGE
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const handleBranchTypeChange =
    (value) => {
      setBranchType(
        value
      );

      setLocation(
        value === "HO"
          ? "HO"
          : "BRANCH"
      );

      setStore("");

      setData([]);
    };

  /* =========================================================
     RESET FILTERS
     CORE FUNCTIONALITY UNCHANGED
  ========================================================= */

  const resetFilters = () => {
    setFromDate("");

    setToDate("");

    setSupplierEquipment(
      ""
    );

    setVendorType("");

    setVehicleMainType(
      ""
    );

    setVehicleType("");

    setEquipmentType("");

    setStatus("");

    setVehicleCode("");

    setSupplier("");

    setStore("");

    setBranchId(
      storedBranchId ||
        "8"
    );

    setLocation(
      branchType === "HO"
        ? "HO"
        : "BRANCH"
    );

    setData([]);
  };

  /* =========================================================
     FILTERED ROWS
  ========================================================= */

  const ledgerRows =
    useMemo(
      () =>
        data.filter(
          (row) =>
            row.SupplierName
        ),
      [data]
    );

  /* =========================================================
     TOTALS
     ORIGINAL CALCULATIONS PRESERVED
  ========================================================= */

  const totalCredit =
    useMemo(
      () =>
        data.reduce(
          (
            sum,
            row
          ) =>
            sum +
            (Number(
              row.Credit_new
            ) ||
              0),
          0
        ),
      [data]
    );

  const totalDebit =
    useMemo(
      () =>
        data.reduce(
          (
            sum,
            row
          ) =>
            sum +
            (Number(
              row.Debit_New
            ) ||
              0),
          0
        ),
      [data]
    );

  const totalClosing =
    useMemo(
      () =>
        data.reduce(
          (
            sum,
            row
          ) =>
            sum +
            (Number(
              row.Cl_Balance_New
            ) ||
              0),
          0
        ),
      [data]
    );

  /* =========================================================
     CURRENCY
  ========================================================= */

  const formatCurrency = (
    amount
  ) => {
    return Number(
      amount || 0
    ).toLocaleString(
      "en-IN",
      {
        style:
          "currency",

        currency:
          "INR",

        minimumFractionDigits:
          2,
      }
    );
  };

  /* =========================================================
     UI
  ========================================================= */

  return (
    <LayoutWrapper>

      {/* =====================================================
          PAGE HEADER
      ===================================================== */}

      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">
            Ledger Main Report
          </span>
        }
        setSkin={
          setSkin
        }
        currentSkin={
          skin
        }
      />

      <div className="space-y-6 mt-6">

        {/* ===================================================
            FILTER CARD
            SAME DESIGN AS FIRST PAGE
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              CARD HEADER
          =============================================== */}

          <div
            className={`px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
              isDark
                ? "border-slate-700/50 bg-slate-900/40"
                : "border-slate-200/50 bg-white/60"
            }`}
          >

            {/* LEFT */}

            <div className="flex items-center gap-3">

              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <BookOpen className="w-5 h-5 text-white" />
              </div>

              <div>

                <h3
                  className={`text-lg font-bold ${
                    isDark
                      ? "text-white"
                      : "text-slate-900"
                  }`}
                >
                  Ledger Main Report
                </h3>

                <p
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-600"
                  }`}
                >
                  View supplier and equipment ledger balances and transactions
                </p>
              </div>
            </div>

            {/* =============================================
                HEADER ACTIONS
            ============================================= */}

            <div className="flex flex-wrap items-center gap-3">

              {/* VIEW WISE
                  EXISTING FUNCTIONALITY PRESERVED
              */}

              <button
                type="button"
                onClick={() =>
                  window.open(
                    "/inventory/LedgerItemWiseReport",
                    "_blank"
                  )
                }
                className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`}
              >
                <FaSearch
                  size={14}
                />

                View Wise
              </button>

              {/* SHOW/HIDE FILTER */}

              <button
                type="button"
                onClick={() =>
                  setShowFilters(
                    !showFilters
                  )
                }
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition ${
                  isDark
                    ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                    : "border-slate-300 text-slate-700 hover:bg-slate-100"
                }`}
              >
                <FaFilter
                  size={14}
                />

                {showFilters
                  ? "Hide Filters"
                  : "Show Filters"}
              </button>
            </div>
          </div>

          {/* ===============================================
              BRANCH TYPE
              ORIGINAL RESTRICTIONS PRESERVED
          =============================================== */}

          <div
            className={`px-6 py-5 border-b ${
              isDark
                ? "border-slate-700/50"
                : "border-slate-200"
            }`}
          >

            <label
              className={
                labelClass
              }
            >
              Branch Type
            </label>

            <div className="flex flex-wrap gap-3 mt-2">

              {/* HEAD OFFICE */}

              <button
                type="button"
                onClick={() =>
                  handleBranchTypeChange(
                    "HO"
                  )
                }
                disabled={
                  storedBranch !==
                    "HEAD-OFFICE" &&
                  storedBranchType !==
                    "HO"
                }
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold border transition ${
                  branchType ===
                  "HO"
                    ? `bg-${baseColor}-${baseShade} text-white border-${baseColor}-${baseShade} shadow`
                    : isDark
                    ? "bg-slate-800 border-slate-600 text-slate-300 hover:bg-slate-700"
                    : "bg-white border-slate-300 text-slate-700 hover:bg-slate-100"
                } ${
                  storedBranch !==
                    "HEAD-OFFICE" &&
                  storedBranchType !==
                    "HO"
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                Head Office
              </button>

              {/* BRANCH */}

              <button
                type="button"
                onClick={() =>
                  handleBranchTypeChange(
                    "Branch"
                  )
                }
                disabled={
                  storedBranch ===
                    "HEAD-OFFICE" ||
                  storedBranchType ===
                    "HO"
                }
                className={`px-5 py-2.5 rounded-lg text-sm font-semibold border transition ${
                  branchType ===
                  "Branch"
                    ? `bg-${baseColor}-${baseShade} text-white border-${baseColor}-${baseShade} shadow`
                    : isDark
                    ? "bg-slate-800 border-slate-600 text-slate-300 hover:bg-slate-700"
                    : "bg-white border-slate-300 text-slate-700 hover:bg-slate-100"
                } ${
                  storedBranch ===
                    "HEAD-OFFICE" ||
                  storedBranchType ===
                    "HO"
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                Branch
              </button>
            </div>
          </div>

          {/* ===============================================
              FILTERS
          =============================================== */}

          {showFilters && (
            <div className="p-6">

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">

                {/* FROM DATE */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    From Date
                  </label>

                  <input
                    type="date"
                    value={
                      fromDate
                    }
                    onChange={(e) =>
                      setFromDate(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* TO DATE */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    To Date
                  </label>

                  <input
                    type="date"
                    value={
                      toDate
                    }
                    onChange={(e) =>
                      setToDate(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  />
                </div>

                {/* SUPPLIER / EQUIPMENT */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Supplier/Equipment
                  </label>

                  <select
                    value={
                      supplierEquipment
                    }
                    onChange={(e) =>
                      setSupplierEquipment(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select
                    </option>

                    <option value="Supplier">
                      Supplier
                    </option>

                    <option value="Equipment">
                      Equipment
                    </option>
                  </select>
                </div>

                {/* =========================================
                    SUPPLIER CONDITIONS
                ========================================= */}

                {supplierEquipment ===
                  "Supplier" && (
                  <>
                    {/* VENDOR TYPE */}

                    <div>

                      <label
                        className={
                          labelClass
                        }
                      >
                        Vendor Type
                      </label>

                      <select
                        value={
                          vendorType
                        }
                        onChange={(e) =>
                          setVendorType(
                            e.target.value
                          )
                        }
                        className={
                          inputClass
                        }
                      >
                        <option value="">
                          Select
                        </option>

                        <option value="All">
                          All
                        </option>

                        <option value="Vendor">
                          Vendor
                        </option>

                        <option value="NonVendor">
                          Non Vendor
                        </option>
                      </select>
                    </div>

                    {/* VEHICLE CODE */}

                    <div>

                      <label
                        className={
                          labelClass
                        }
                      >
                        Vehicle Code
                      </label>

                      <input
                        type="text"
                        value={
                          vehicleCode
                        }
                        onChange={(e) =>
                          setVehicleCode(
                            e.target.value
                          )
                        }
                        placeholder="Enter vehicle code"
                        className={
                          inputClass
                        }
                      />
                    </div>

                    {/* SUPPLIER */}

                    <div>

                      <label
                        className={
                          labelClass
                        }
                      >
                        Supplier
                      </label>

                      <select
                        value={
                          supplier
                        }
                        onChange={(e) =>
                          setSupplier(
                            e.target.value
                          )
                        }
                        className={
                          inputClass
                        }
                      >
                        <option value="">
                          Select
                        </option>

                        {supplierList.map(
                          (s) => (
                            <option
                              key={
                                s.fld_supplierid
                              }
                              value={
                                s.fld_supplierid
                              }
                            >
                              {
                                s.fld_suppliername
                              }
                            </option>
                          )
                        )}
                      </select>
                    </div>
                  </>
                )}

                {/* =========================================
                    EQUIPMENT CONDITION
                ========================================= */}

                {supplierEquipment ===
                  "Equipment" && (
                  <div>

                    <label
                      className={
                        labelClass
                      }
                    >
                      Equipment Type
                    </label>

                    <select
                      value={
                        equipmentType
                      }
                      onChange={(e) =>
                        setEquipmentType(
                          e.target.value
                        )
                      }
                      className={
                        inputClass
                      }
                    >
                      <option value="">
                        Select
                      </option>

                      <option value="Hire">
                        Hire
                      </option>

                      <option value="Own">
                        Own
                      </option>

                      <option value="Department">
                        Department
                      </option>

                      <option value="SpareReturn">
                        Spare Return
                      </option>

                      <option value="CrusherPlant">
                        Crusher Plant
                      </option>
                    </select>
                  </div>
                )}

                {/* BRANCH */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Branch
                  </label>

                  <select
                    value={
                      store
                    }
                    onChange={(e) => {
                      setStore(
                        e.target.value
                      );

                      setBranchId(
                        e.target.value
                      );
                    }}
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select
                    </option>

                    {branchList.map(
                      (b) => (
                        <option
                          key={
                            b.fld_storeid
                          }
                          value={
                            b.fld_storeid
                          }
                        >
                          {
                            b.fld_storename
                          }
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* VEHICLE MAIN TYPE */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Vehicle Main Type
                  </label>

                  <select
                    value={
                      vehicleMainType
                    }
                    onChange={(e) =>
                      setVehicleMainType(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select
                    </option>

                    <option value="high">
                      High
                    </option>

                    <option value="low">
                      Low
                    </option>
                  </select>
                </div>

                {/* VEHICLE TYPE */}

                <div>

                  <label
                    className={
                      labelClass
                    }
                  >
                    Vehicle Type
                  </label>

                  <select
                    value={
                      vehicleType
                    }
                    onChange={(e) =>
                      setVehicleType(
                        e.target.value
                      )
                    }
                    className={
                      inputClass
                    }
                  >
                    <option value="">
                      Select
                    </option>
                  </select>
                </div>
              </div>

              {/* =============================================
                  ACTION BUTTONS
              ============================================= */}

              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">

                {/* RESET */}

                <button
                  type="button"
                  onClick={
                    resetFilters
                  }
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <FaRedo
                    size={14}
                  />

                  Clear Filters
                </button>

                {/* SEARCH */}

                <button
                  type="button"
                  onClick={
                    handleSearch
                  }
                  disabled={
                    loading
                  }
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-50`}
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FaSearch
                      size={14}
                    />
                  )}

                  {loading
                    ? "Loading..."
                    : "Search Records"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ===================================================
            RESULTS CARD
        =================================================== */}

        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >

          {/* ===============================================
              RESULTS HEADER
          =============================================== */}

          <div className="p-6 border-b border-gray-200 dark:border-gray-700">

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">

              <div className="flex items-center gap-3">

                <div
                  className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                />

                <h3 className="text-xl font-bold">
                  Ledger Report Results
                </h3>

                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark
                      ? "bg-gray-700 text-gray-300"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {ledgerRows.length} records
                </span>
              </div>

              {ledgerRows.length >
                0 && (
                <span
                  className={`text-xs ${
                    isDark
                      ? "text-slate-400"
                      : "text-slate-500"
                  }`}
                >
                  Last updated:{" "}
                  {new Date().toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>

          {/* ===============================================
              LOADING
          =============================================== */}

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">

              <div
                className={`w-8 h-8 border-4 border-${baseColor}-200 border-t-${baseColor}-600 rounded-full animate-spin`}
              />

              <p
                className={`mt-3 text-sm ${
                  isDark
                    ? "text-slate-400"
                    : "text-slate-500"
                }`}
              >
                Loading ledger records...
              </p>
            </div>
          ) : ledgerRows.length >
            0 ? (

            /* =============================================
               TABLE
            ============================================= */

            <div className="overflow-x-auto">

              <table className="w-full text-sm">

                {/* HEADER */}

                <thead
                  className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                >
                  <tr>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      S.No
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Supplier
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Code
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      Vendor Type
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                      Credit
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                      City
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                      Debit
                    </th>

                    <th className="px-4 py-3 text-right text-xs font-bold uppercase">
                      Cl. Balance
                    </th>

                    <th className="px-4 py-3 text-center text-xs font-bold uppercase">
                      View
                    </th>
                  </tr>
                </thead>

                {/* BODY */}

                <tbody
                  className={`divide-y ${
                    isDark
                      ? "divide-gray-600"
                      : "divide-gray-200"
                  }`}
                >
                  {ledgerRows.map(
                    (
                      row,
                      index
                    ) => (
                      <tr
                        key={
                          index
                        }
                        className={`transition ${
                          isDark
                            ? "bg-gray-800/20 hover:bg-gray-700/30"
                            : "bg-white hover:bg-gray-50"
                        }`}
                      >

                        {/* S NO */}

                        <td className="px-4 py-3 font-medium">
                          {index + 1}
                        </td>

                        {/* SUPPLIER */}

                        <td className="px-4 py-3 font-medium">
                          {row.SupplierName ||
                            "-"}
                        </td>

                        {/* CODE */}

                        <td className="px-4 py-3">

                          <span
                            className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                              isDark
                                ? "bg-gray-700 text-gray-200"
                                : `bg-${baseColor}-100 text-${baseColor}-800`
                            }`}
                          >
                            {row.Code ||
                              "-"}
                          </span>
                        </td>

                        {/* VENDOR TYPE */}

                        <td className="px-4 py-3">
                          {row.VendorType ||
                            "-"}
                        </td>

                        {/* CREDIT */}

                        <td
                          className={`px-4 py-3 whitespace-nowrap text-right font-semibold ${
                            isDark
                              ? "text-green-300"
                              : "text-green-700"
                          }`}
                        >
                          {formatCurrency(
                            row.Credit_new
                          )}
                        </td>

                        {/* CITY */}

                        <td className="px-4 py-3">
                          {row.City ||
                            "-"}
                        </td>

                        {/* DEBIT */}

                        <td
                          className={`px-4 py-3 whitespace-nowrap text-right font-semibold ${
                            isDark
                              ? "text-red-300"
                              : "text-red-700"
                          }`}
                        >
                          {formatCurrency(
                            row.Debit_New
                          )}
                        </td>

                        {/* CLOSING */}

                        <td className="px-4 py-3 whitespace-nowrap text-right font-semibold">
                          {formatCurrency(
                            row.Cl_Balance_New
                          )}
                        </td>

                        {/* VIEW SUB REPORT */}

                        <td className="px-4 py-3 text-center">

                          <button
                            type="button"
                            onClick={() =>
                              handleOpenSubReport(
                                row
                              )
                            }
                            className={`inline-flex items-center justify-center p-1.5 rounded-full transition ${
                              isDark
                                ? "bg-blue-900/30 text-blue-300 hover:bg-blue-900/50"
                                : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                            }`}
                            title="View Ledger Details"
                          >
                            <FaEye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>

                {/* =========================================
                    TOTAL FOOTER
                ========================================= */}

                <tfoot
                  className={
                    isDark
                      ? "bg-slate-900/70"
                      : "bg-slate-100"
                  }
                >
                  <tr>

                    <td
                      colSpan="4"
                      className={`px-4 py-3 text-right text-sm font-bold ${
                        isDark
                          ? "text-white"
                          : "text-slate-900"
                      }`}
                    >
                      TOTAL
                    </td>

                    <td
                      className={`px-4 py-3 whitespace-nowrap text-right text-sm font-bold ${
                        isDark
                          ? "text-green-300"
                          : "text-green-700"
                      }`}
                    >
                      {formatCurrency(
                        totalCredit
                      )}
                    </td>

                    <td></td>

                    <td
                      className={`px-4 py-3 whitespace-nowrap text-right text-sm font-bold ${
                        isDark
                          ? "text-red-300"
                          : "text-red-700"
                      }`}
                    >
                      {formatCurrency(
                        totalDebit
                      )}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-bold">
                      {formatCurrency(
                        totalClosing
                      )}
                    </td>

                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (

            /* ===============================================
               EMPTY STATE
            =============================================== */

            <div className="px-4 py-14 text-center">

              <FaBoxOpen
                size={40}
                className={`mx-auto mb-3 ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-400"
                }`}
              />

              <h3
                className={`text-sm font-semibold ${
                  isDark
                    ? "text-slate-300"
                    : "text-slate-700"
                }`}
              >
                No ledger records found
              </h3>

              <p
                className={`mt-1 text-sm ${
                  isDark
                    ? "text-slate-500"
                    : "text-slate-500"
                }`}
              >
                Try adjusting your search filters or search with different criteria.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* =====================================================
          TOAST
      ===================================================== */}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={
          isDark
            ? "dark"
            : "light"
        }
      />
    </LayoutWrapper>
  );
};

export default LedgerMainReport;